import * as React from 'react';

import {formEncodedApi} from 'nutshell-core/api';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Card} from 'shells/card/card';
import {PeepTwoLine} from 'shells/peep/peep-two-line';
import {Popover, PopoverComponent} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';

import type {PeepSendToDestinations as PeepSendToDestinationsFragment} from 'nutshell-graphql-types';

import {QuickbooksConnectCTA, handleCtaClick} from './quickbooks-connect-cta';

import styles from './quickbooks-popover.module.css';

type Props = {
    onCancel: () => void;
    onSuccess: () => void;
    onSubmit: () => Promise<any>;
    canAccessIntegrations: boolean;
    peep: PeepSendToDestinationsFragment;
} & JSX.LibraryManagedAttributes<
    typeof PopoverComponent,
    React.ComponentProps<typeof PopoverComponent>
>;

export const QuickbooksPeepPopover = (props: Props) => {
    const [isQuickbooksConnected, setQuickbooksConnected] = React.useState<boolean>(
        NutClientConfig.integrations && NutClientConfig.integrations.quickbooksEnabled
    );
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    function handleSubmit() {
        setIsSubmitting(true);

        props
            .onSubmit()
            .then(() => {
                setIsSubmitting(false);
            })
            .catch((err) => {
                setIsSubmitting(false);
                if (err && err.message) {
                    setErrorMessage(safelyGetGraphQLErrorMessage(err));
                } else {
                    setErrorMessage(
                        'Sorry, something went wrong. Could not connect with Quickbooks'
                    );
                }
            });
    }

    function handleSaveComplete() {
        props.onSuccess();
    }

    function getPeepCustomerId() {
        const customerResource = props.peep.externalResources.find((externalResource) => {
            return externalResource.externalResourceType === 'QUICKBOOKS_CUSTOMER';
        });

        return customerResource ? customerResource.externalId : null;
    }

    if (errorMessage) {
        return (
            <Popover
                anchor={props.anchor}
                isDialog={true}
                onBlur={() => {
                    setErrorMessage(null);
                    props.onCancel();
                }}
                bodyColor='error'
            >
                {errorMessage}
            </Popover>
        );
    }

    const customerId = getPeepCustomerId();

    return (
        <Popover anchor={props.anchor} onBlur={props.onCancel} location={props.location}>
            {isQuickbooksConnected ? (
                <PopoverForm
                    width={340}
                    onClose={props.onCancel}
                    title={
                        <div className={styles['logo-title']}>
                            <img
                                alt='Quickbooks'
                                src='/include/images/integration-logos/quickbooks.png'
                                height={16}
                                style={{marginRight: 8}}
                            />
                            Send to Quickbooks
                        </div>
                    }
                    onSubmit={handleSubmit}
                    saveButtonText={customerId ? 'Update customer' : 'Create customer'}
                    isSaving={isSubmitting}
                    onClearSaveComplete={handleSaveComplete}
                    saveButtonSavingText='Sending'
                    saveButtonSuccessText='Sent!'
                >
                    <Card hasBorder={true}>
                        <PeepTwoLine
                            name={props.peep.name}
                            avatarUrl={props.peep.avatarUrl}
                            initials={props.peep.initials}
                            htmlUrl={props.peep.htmlUrl}
                            type={props.peep.type}
                            relatedEntityName=''
                            isLinkable={false}
                            shouldClientRoute={false}
                        />
                    </Card>
                    {customerId ? (
                        <span style={{marginTop: '15px', display: 'block'}}>{`This ${
                            props.peep.type === 'accounts' ? 'company' : 'person'
                        } has already been added to Quickbooks as a customer, would you like to update it?`}</span>
                    ) : undefined}
                </PopoverForm>
            ) : (
                <QuickbooksConnectCTA
                    canAccessIntegrations={props.canAccessIntegrations}
                    onClick={() => {
                        handleCtaClick().then(() => {
                            formEncodedApi
                                .get('/integrations/status')
                                // @ts-expect-error
                                .then((res) => res.json())
                                .then((json) => {
                                    if (
                                        json.integrationsEnabled &&
                                        json.integrationsEnabled.quickbooks
                                    ) {
                                        setQuickbooksConnected(true);
                                        // For the next time the popover gets opened, before a full refresh
                                        if (NutClientConfig.integrations) {
                                            NutClientConfig.integrations.quickbooksEnabled = true;
                                        }
                                    } else {
                                        props.onCancel();
                                    }
                                })
                                .catch(() => {
                                    // Just close the popover if something goes wrong.
                                    props.onCancel();
                                });
                        });
                    }}
                />
            )}
        </Popover>
    );
};
